import React, {useMemo} from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";

const LINE_CATEGORY_ORDER = ["점검", "작업", "장애"];

const GAUGE_COLORS = ["#F44336", "#FF8A65", "#FFD54F", "#AED581", "#4CAF50"];
const GAUGE = {
    width: 280,
    height: 116,
    cx: 140,
    cy: 98,
    innerR: 45,
    outerR: 90,
};

const GAUGE_FOOTER_H = 32;
const LINE_CHART_X_HEIGHT = 22;

const calcKeepRatePercent = (left) => {
    if (!left) return 0;
    const denominator = Number(left.denominator) || 0;
    const cnt = Number(left.cnt) || 0;
    if (denominator === 0) return 0;
    return Math.round((cnt / denominator) * 100);
};

const polar = (cx, cy, radius, angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    return {
        x: cx + radius * Math.cos(rad),
        y: cy + radius * Math.sin(rad),
    };
};

const describeDonutSegment = (cx, cy, innerR, outerR, startDeg, endDeg) => {
    const startOuter = polar(cx, cy, outerR, startDeg);
    const endOuter = polar(cx, cy, outerR, endDeg);
    const endInner = polar(cx, cy, innerR, endDeg);
    const startInner = polar(cx, cy, innerR, startDeg);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;

    return [
        `M ${startOuter.x} ${startOuter.y}`,
        `A ${outerR} ${outerR} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}`,
        `L ${endInner.x} ${endInner.y}`,
        `A ${innerR} ${innerR} 0 ${largeArc} 0 ${startInner.x} ${startInner.y}`,
        "Z",
    ].join(" ");
};

const valueToAngle = (value) => {
    const clamped = Math.min(100, Math.max(0, value));
    return 180 + (clamped / 100) * 180;
};

function ComplyRateTooltip({active, payload, label}) {
    if (!active || !payload?.length) return null;
    const rate = Number(payload[0]?.value) || 0;
    return (
        <div className="rounded-[4px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="font-medium text-[#333]">{label}</div>
            <div className="text-[#666]">준수율 : {(rate * 100).toFixed(0)}%</div>
        </div>
    );
}

/** 참조 pin: 위 뾰족(직선) + 아래 반원 밑동 + 중앙 흰 구멍 */
const buildPinNeedle = (cx, baseY) => {
    const r = 5.5;
    const tipY = baseY - 28;

    return {
        path: [
            `M ${cx} ${tipY}`,
            `L ${cx + r} ${baseY}`,
            `A ${r} ${r} 0 1 1 ${cx - r} ${baseY}`,
            "Z",
        ].join(" "),
        holeCy: baseY + 1,
        holeR: 3,
    };
};

function CustomComplyGauge({
    value,
    title,
    className = "h-full w-full max-w-full",
    preserveAspectRatio = "xMidYMax meet",
}) {
    const {width, height, cx, cy, innerR, outerR} = GAUGE;
    const needleRotation = valueToAngle(value) - 270;
    const pivotY = cy - 20;
    const needle = buildPinNeedle(cx, pivotY);
    const labelText = title ?? "업무준수율";

    const segments = useMemo(
        () =>
            GAUGE_COLORS.map((fill, index) => {
                const startDeg = 180 + index * 36;
                const endDeg = startDeg + 36;
                return {
                    fill,
                    d: describeDonutSegment(cx, cy, innerR, outerR, startDeg, endDeg),
                };
            }),
        [cx, cy, innerR, outerR]
    );

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            className={className}
            preserveAspectRatio={preserveAspectRatio}
            aria-hidden
        >
            {segments.map((seg, index) => (
                <path key={`gauge-seg-${index}`} d={seg.d} fill={seg.fill}/>
            ))}

            <g transform={`rotate(${needleRotation}, ${cx}, ${pivotY})`}>
                <path d={needle.path} fill="#6d6d6d"/>
                <circle cx={cx} cy={needle.holeCy} r={needle.holeR} fill="#ffffff"/>
            </g>

            <text
                x={cx}
                y={95}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#333333"
                fontSize="10"
                fontWeight="500"
            >
                {labelText}
            </text>
        </svg>
    );
}

export function ComplyRateGauge({value, title, centered = false, compact = false}) {
    if (centered) {
        return (
            <div className={`flex h-full w-full min-h-0 flex-col items-center justify-center ${compact ? "gap-1" : "gap-2"}`}>
                <div className="flex min-h-0 w-full flex-1 items-center justify-center px-1">
                    <CustomComplyGauge
                        value={value}
                        title={title}
                        className={`h-full w-full max-h-full max-w-full ${compact ? "min-h-[72px]" : "min-h-[100px]"}`}
                        preserveAspectRatio="xMidYMid meet"
                    />
                </div>
                <span className={`shrink-0 rounded-full border border-[#93c5fd] bg-white font-bold leading-none text-[#3b82f6] shadow-[0_6px_6px_rgba(59,130,246,0.3)] ${compact ? "px-3 py-1 text-[15px]" : "px-[18px] py-[8px] text-[20px]"}`}>
                    {value}%
                </span>
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col">
            <div className="flex min-h-0 flex-1 items-end justify-center">
                <CustomComplyGauge value={value} title={title}/>
            </div>
            <div
                className="flex shrink-0 items-end justify-center"
                style={{height: GAUGE_FOOTER_H}}
            >
                <span className="rounded-full border border-[#93c5fd] bg-white px-[16px] py-[8px] text-[18px] font-bold leading-none text-[#3b82f6] shadow-[0_6px_6px_rgba(59,130,246,0.3)]">
                    {value}%
                </span>
            </div>
        </div>
    );
}

function ComplyRateLineChart({items}) {
    const chartData = useMemo(
        () =>
            LINE_CATEGORY_ORDER.map((name) => {
                const row = items.find((item) => item.name === name);
                return {
                    name,
                    rate: row ? Number(row.cnt) / 100 : 0,
                };
            }),
        [items]
    );

    return (
        <div className="flex h-full w-full flex-col">
            <div className="min-h-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                        <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e8e8e8"/>
                        <XAxis
                            dataKey="name"
                            height={LINE_CHART_X_HEIGHT}
                            tickMargin={2}
                            tick={{fontSize: 11, fill: "#888"}}
                            axisLine={{stroke: "#666666", strokeWidth: 1}}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 1]}
                            ticks={[0, 0.3, 0.5, 0.7, 1]}
                            tick={{fontSize: 11, fill: "#888"}}
                            axisLine={{stroke: "#666666", strokeWidth: 1}}
                            tickLine={false}
                            width={28}
                        />
                        <Tooltip content={<ComplyRateTooltip/>}/>
                        <Line
                            type="monotone"
                            dataKey="rate"
                            stroke="#666666"
                            strokeWidth={2}
                            strokeDasharray="6 4"
                            dot={{r: 4, fill: "#6B5CA5", stroke: "#fff", strokeWidth: 2}}
                            activeDot={{r: 5}}
                            name="DATA"
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="flex shrink-0 items-center justify-center pt-0.5 text-[11px] leading-none text-[#888]">
                <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-[2px] w-4 rounded bg-[#6B5CA5]"/>
                    <span className="inline-block h-2 w-2 rounded-full border-2 border-[#6B5CA5] bg-white"/>
                    DATA
                </span>
            </div>
        </div>
    );
}

function TaskComplyRateStaticContent({data}) {
    const percent = useMemo(() => calcKeepRatePercent(data?.left), [data?.left]);
    const lineItems = data?.right ?? [];

    return (
        <div className="task-comply-rate-static flex h-full min-h-0 flex-1 w-full flex-col bg-white [&_*:focus]:outline-none [&_.recharts-active-dot]:outline-none">
            <h3 className="shrink-0 text-[21px] font-bold leading-none text-[#333]">
                {data?.title ?? "유지보수 업무준수율"}
            </h3>

            <div className="mt-[10px] flex min-h-0 flex-1 items-stretch">
                <div className="flex h-full w-full md:w-[46%] min-w-0 shrink-0 flex-col">
                    <ComplyRateGauge value={percent} title={data?.left?.title}/>
                </div>
                <div className="hidden md:flex h-full min-w-0 flex-1 flex-col overflow-hidden md:pl-[8px]">
                    <ComplyRateLineChart items={lineItems}/>
                </div>
            </div>
        </div>
    );
}

export function TaskComplyRateStatic({data, loading = false}) {
    if (!data) {
        return (
            <div className="task-comply-rate-static relative flex h-full min-h-0 flex-1 w-full flex-col bg-white">
                {loading && <ChartLoadingOverlay/>}
            </div>
        );
    }

    return <TaskComplyRateStaticContent data={data}/>;
}

export default TaskComplyRateStatic;
